import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useFx } from "../lib/fx";
import { useI18n } from "../lib/i18n";
import {
  Verified, Star, Plus, Pencil, Trash2, Archive, Undo2, AlertTriangle,
  MessageSquare, Search, Share2, Check, ExternalLink, Image as ImageIcon,
  Calendar, Eye, LayoutGrid, CalendarDays, ListChecks, Briefcase, Users, Wallet, Globe,
} from "lucide-react";
import ServiceFormModal from "../components/ServiceFormModal";
import OpenDisputeModal from "../components/OpenDisputeModal";
import ChatDrawer from "../components/ChatDrawer";
import ProviderBookingCalendar from "../components/ProviderBookingCalendar";
import ServiceCalendarModal from "../components/ServiceCalendarModal";
import TeamSection from "../components/TeamSection";
import WalletSection from "../components/WalletSection";

const TABS = [
  { id: "overview", label_key: "tab_overview",  fallback: "Overview", Icon: LayoutGrid },
  { id: "calendar", label_key: "tab_calendar",  fallback: "Calendar", Icon: CalendarDays },
  { id: "bookings", label_key: "tab_bookings",  fallback: "Bookings", Icon: ListChecks },
  { id: "services", label_key: "tab_services",  fallback: "Services", Icon: Briefcase },
  { id: "team",     label_key: "tab_team",      fallback: "Team",     Icon: Users },
  { id: "wallet",   label_key: "tab_wallet",    fallback: "Wallet",   Icon: Wallet },
  { id: "page",     label_key: "tab_page",      fallback: "Public page", Icon: Globe },
];

const BOOKING_FILTERS = [
  { id: "all", label_key: "f_all", fallback: "All" },
  { id: "requested", label_key: "f_new_requests", fallback: "New requests" },
  { id: "paid_reserved", label_key: "f_upcoming", fallback: "Upcoming" },
  { id: "completed_confirmed", label_key: "f_completed", fallback: "Completed" },
  { id: "funds_released", label_key: "f_paid_out", fallback: "Paid out" },
  { id: "in_dispute", label_key: "f_in_dispute", fallback: "In dispute" },
];

const SERVICE_FILTERS = [
  { id: "all", label_key: "f_all", fallback: "All" },
  { id: "published", label_key: "f_published", fallback: "Published" },
  { id: "pending_admin_review", label_key: "f_pending", fallback: "Pending review" },
  { id: "archived", label_key: "f_archived", fallback: "Archived" },
];

export default function ProviderDashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { format } = useFx();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const [tab, setTab] = useState("overview");
  const [calendar, setCalendar] = useState(null);
  const [bFilter, setBFilter] = useState("all");
  const [bQuery, setBQuery] = useState("");
  const [sFilter, setSFilter] = useState("all");
  const [sQuery, setSQuery] = useState("");
  const [shareCopied, setShareCopied] = useState(false);

  const [destinations, setDestinations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formMode, setFormMode] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [calendarService, setCalendarService] = useState(null);
  const [disputeBooking, setDisputeBooking] = useState(null);
  const [chatBookingId, setChatBookingId] = useState(null);
  const [flash, setFlash] = useState(null);

  const PROV_STATUSES = useMemo(() => ({
    requested:                 { label: t("provider_dash.new_requests",     "New request"),         chip: "info" },
    pending_user_payment:      { label: t("user_dash.awaiting_payment",     "Awaiting payment"),    chip: "warning" },
    paid_reserved:             { label: t("booking_status.paid_reserved",   "Paid · upcoming"),     chip: "success" },
    completed_confirmed:       { label: t("booking_status.completed_confirmed", "Completed"),       chip: "success" },
    funds_released:            { label: t("booking_status.funds_released",  "Paid out"),            chip: "success" },
    provider_rejected:         { label: t("booking_status.provider_rejected", "You declined"),      chip: "danger" },
    cancelled_by_user:         { label: t("booking_status.cancelled_by_user", "Cancelled"),         chip: "" },
  }), [t]);

  const reload = async () => {
    try {
      const { data } = await api.get("/providers/me");
      setData(data);
    } catch (e) { setError(formatErr(e)); }
  };

  const loadCalendar = async () => {
    try {
      const { data } = await api.get("/providers/me/bookings/calendar");
      setCalendar(data);
    } catch { /* ignore — calendar is non-blocking */ }
  };

  useEffect(() => { reload(); loadCalendar(); }, []);
  useEffect(() => {
    if (tab === "calendar" || tab === "overview") loadCalendar();
  }, [tab]);

  useEffect(() => {
    (async () => {
      try {
        const [dRes, cRes] = await Promise.all([
          api.get("/destinations"),
          api.get("/categories"),
        ]);
        setDestinations(dRes.data.items || []);
        setCategories(cRes.data.items || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const act = async (id, path, body = {}) => {
    setBusy(id); setError(null);
    try { await api.post(`/bookings/${id}/${path}`, body); await reload(); }
    catch (e) { setError(formatErr(e)); }
    finally { setBusy(null); }
  };

  const openCreate = () => { setEditingService(null); setFormMode("create"); };
  const openEdit = (s) => { setEditingService(s); setFormMode("edit"); };
  const closeForm = (refresh) => {
    if (refresh) {
      setFlash(formMode === "edit"
        ? t("provider_dash.service_updated", "Service updated.")
        : t("provider_dash.service_created_pending", "Service submitted — pending concierge review."));
      reload();
      setTimeout(() => setFlash(null), 4500);
    }
    setFormMode(null);
    setEditingService(null);
  };

  const deleteService = async (s) => {
    if (!window.confirm(t("provider_dash.delete_confirm", "Delete this service permanently?"))) return;
    setBusy(s.id); setError(null);
    try {
      await api.delete(`/services/${s.id}`);
      setFlash(t("provider_dash.service_deleted", "Service deleted."));
      setTimeout(() => setFlash(null), 4500);
      await reload();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(null); }
  };

  const archiveService = async (s, archive) => {
    setBusy(s.id); setError(null);
    try {
      await api.post(`/services/${s.id}/${archive ? "archive" : "unarchive"}`);
      await reload();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(null); }
  };

  const services = data?.services || [];
  const bookings = data?.bookings || [];
  const p = data?.provider;

  const publicLink = useMemo(() => {
    if (!p?.slug && !p?.id) return "";
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/p/${p.slug || p.id}`;
  }, [p]);

  const copyShare = async () => {
    if (!publicLink) return;
    try {
      await navigator.clipboard.writeText(publicLink);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const filteredBookings = useMemo(() => {
    const q = bQuery.trim().toLowerCase();
    return bookings.filter((b) => {
      if (bFilter === "in_dispute") {
        if (!(b.dispute && b.dispute.status === "open")) return false;
      } else if (bFilter !== "all" && b.status !== bFilter) {
        return false;
      }
      if (!q) return true;
      return (
        (b.service_title || "").toLowerCase().includes(q) ||
        (b.user_name || "").toLowerCase().includes(q) ||
        (b.user_email || "").toLowerCase().includes(q) ||
        (b.id || "").toLowerCase().includes(q)
      );
    });
  }, [bookings, bFilter, bQuery]);

  const filteredServices = useMemo(() => {
    const q = sQuery.trim().toLowerCase();
    return services.filter((s) => {
      if (sFilter !== "all" && s.status !== sFilter) return false;
      if (!q) return true;
      return (
        (s.title || "").toLowerCase().includes(q) ||
        (s.description || "").toLowerCase().includes(q) ||
        (s.location || "").toLowerCase().includes(q)
      );
    });
  }, [services, sFilter, sQuery]);

  if (!data) return <div className="pt-32 px-6 text-muted">{t("common.loading", "Loading…")}</div>;

  if (!data.provider) {
    return (
      <div className="pt-32 px-6 max-w-xl mx-auto text-center">
        <div className="font-script text-5xl text-teal mb-3">{t("provider_dash.not_a_provider_title", "You're not a provider yet.")}</div>
        <p className="text-muted text-sm mb-6">{t("provider_dash.not_a_provider_body", "Apply to join our network of verified Ibiza providers.")}</p>
        <Link to="/become-provider" className="btn btn-primary">{t("provider_dash.apply_now", "Apply now")} →</Link>
      </div>
    );
  }

  const statusLabel = p.status === "approved"
    ? t("provider_dash.approved_verified", "Approved & verified")
    : t("provider_dash.pending_review", "Pending concierge review");

  return (
    <div className="pt-28 pb-24 px-5 md:px-10 mx-auto max-w-7xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow flex items-center gap-3">
            <span className="role-badge provider">Provider</span> {t("common.dashboard", "Dashboard")}
          </div>
          <h1 className="section-title text-4xl md:text-5xl mt-3 flex items-center gap-3">
            {p.name}
            {p.verified && <Verified className="text-teal" size={28}/>}
          </h1>
          <div className="text-sm text-muted mt-1 flex items-center gap-3 flex-wrap">
            <span className={`chip ${p.status === "approved" ? "success" : "warning"}`}>{statusLabel}</span>
            {p.rating_avg > 0 && (
              <span className="flex items-center gap-1">
                <Star size={12} className="fill-champagne text-champagne"/>
                {t("provider_dash.rating_summary", "{{avg}} avg · {{count}} reviews", { avg: p.rating_avg, count: p.rating_count })}
              </span>
            )}
          </div>
        </div>

        {p.status === "approved" && publicLink && (
          <div className="flex flex-wrap gap-2" data-testid="prov-share-box">
            <button onClick={copyShare} className="btn btn-primary" data-testid="prov-share-copy">
              {shareCopied ? <Check size={14} /> : <Share2 size={14} />}
              {shareCopied
                ? t("provider_dash.share_copied", "Link copied")
                : t("provider_dash.share_my_page", "Share my page")}
            </button>
            <Link to={`/p/${p.slug || p.id}`} className="btn btn-outline" data-testid="prov-share-view">
              <ExternalLink size={14} /> {t("provider_dash.view_public_page", "View")}
            </Link>
          </div>
        )}
      </div>

      {error && <div className="text-rose-300 text-sm mb-4" data-testid="prov-error">{error}</div>}
      {flash && <div className="luxe-card p-4 text-sm text-teal border-teal/40 mb-4" data-testid="prov-flash">{flash}</div>}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="prov-tabs">
        {TABS.map((tb) => {
          const I = tb.Icon;
          return (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`tab-pill inline-flex items-center gap-1.5 ${tab === tb.id ? "active" : ""}`}
              data-testid={`prov-tab-${tb.id}`}
            >
              {I && <I size={13} />}
              {t(`provider_dash.${tb.label_key}`, tb.fallback)}
            </button>
          );
        })}
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <div data-testid="prov-overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              [t("provider_dash.active_services", "Active services"), services.filter(s => s.status === "published").length],
              [t("provider_dash.new_requests",    "New requests"),    bookings.filter(b => b.status === "requested").length],
              [t("provider_dash.paid_reserved",   "Paid bookings"),   bookings.filter(b => b.status === "paid_reserved").length],
              [t("provider_dash.completed",       "Completed"),       bookings.filter(b => ["completed_confirmed","funds_released"].includes(b.status)).length],
            ].map(([label, v]) => (
              <div key={label} className="luxe-card p-5">
                <div className="label-tiny">{label}</div>
                <div className="font-serif-display text-3xl mt-1">{v}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <button onClick={() => setTab("bookings")} className="luxe-card p-6 text-left hover:border-teal/40 transition-colors" data-testid="overview-go-bookings">
              <div className="eyebrow">{t("provider_dash.tab_bookings", "Bookings")}</div>
              <div className="font-serif-display text-2xl mt-2">
                {t("provider_dash.overview_bookings_cta", "Manage {{count}} booking(s)", { count: bookings.length })}
              </div>
              <p className="text-sm text-muted mt-2">
                {t("provider_dash.overview_bookings_body", "Confirm, decline, message guests, open disputes.")}
              </p>
            </button>
            <button onClick={() => setTab("services")} className="luxe-card p-6 text-left hover:border-teal/40 transition-colors" data-testid="overview-go-services">
              <div className="eyebrow">{t("provider_dash.tab_services", "Services")}</div>
              <div className="font-serif-display text-2xl mt-2">
                {t("provider_dash.overview_services_cta", "{{count}} listing(s)", { count: services.length })}
              </div>
              <p className="text-sm text-muted mt-2">
                {t("provider_dash.overview_services_body", "Add, edit, archive, upload photos. Self-edit anytime.")}
              </p>
            </button>
          </div>
        </div>
      )}

      {/* Calendar */}
      {tab === "calendar" && (
        <div data-testid="prov-calendar-tab">
          {!calendar ? (
            <div className="luxe-card p-10 text-center text-muted text-sm">
              {t("common.loading", "Loading…")}
            </div>
          ) : (
            <ProviderBookingCalendar
              items={calendar.items}
              todayISO={calendar.today}
              statusChip={(status) => PROV_STATUSES[status] || { label: status, chip: "" }}
            />
          )}

          {calendar?.items?.length > 0 && (
            <div className="grid md:grid-cols-3 gap-4 mt-6" data-testid="prov-cal-buckets">
              {[
                ["upcoming", t("calendar.bucket_upcoming", "Upcoming"), "success"],
                ["current",  t("calendar.bucket_current",  "In progress"), "warning"],
                ["past",     t("calendar.bucket_past",     "Past"), ""],
              ].map(([bucket, label, chip]) => {
                const list = calendar.items.filter((b) => b.bucket === bucket);
                return (
                  <div key={bucket} className="luxe-card p-5" data-testid={`prov-cal-bucket-${bucket}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className={`chip ${chip}`}>{label}</span>
                      <span className="text-xs text-muted font-mono">{list.length}</span>
                    </div>
                    {list.length === 0 ? (
                      <div className="text-xs text-muted">
                        {t("calendar.bucket_empty", "Nothing here.")}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {list.slice(0, 5).map((b) => (
                          <div key={b.id} className="text-sm">
                            <div className="font-serif-display text-base truncate">{b.service_title}</div>
                            <div className="text-[11px] text-muted">
                              {b.start_date || "—"} · {b.user_name || b.user_email}
                            </div>
                          </div>
                        ))}
                        {list.length > 5 && (
                          <div className="text-[11px] text-muted">
                            {t("calendar.bucket_more", "+ {{count}} more", { count: list.length - 5 })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Bookings */}
      {tab === "bookings" && (
        <div data-testid="prov-bookings">
          <div className="luxe-card p-4 md:p-5 mb-5 flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
              <input
                value={bQuery}
                onChange={(e) => setBQuery(e.target.value)}
                placeholder={t("provider_dash.bookings_search_ph", "Search by guest, service or booking ID…")}
                className="input-bare !pl-10"
                data-testid="prov-bookings-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {BOOKING_FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setBFilter(f.id)}
                  className={`tab-pill ${bFilter === f.id ? "active" : ""}`}
                  data-testid={`prov-bookings-filter-${f.id}`}
                >
                  {t(`provider_dash.${f.label_key}`, f.fallback)}
                </button>
              ))}
            </div>
          </div>

          {filteredBookings.length === 0 ? (
            <div className="luxe-card p-10 text-center text-muted text-sm">
              {bookings.length === 0
                ? t("user_dash.no_bookings_title", "No bookings yet.")
                : t("provider_dash.no_match_filters", "No bookings match your filters.")}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredBookings.map((b) => {
                const s = PROV_STATUSES[b.status] || { label: b.status, chip: "" };
                const inDispute = b.dispute && b.dispute.status === "open";
                const canDispute = ["paid_reserved", "in_progress", "completed_confirmed"].includes(b.status) && !inDispute;
                return (
                  <div key={b.id} className="luxe-card p-5" data-testid={`pbook-${b.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`chip ${s.chip}`}>{s.label}</span>
                          {inDispute && (
                            <span className="chip danger flex items-center gap-1" data-testid={`pdispute-chip-${b.id}`}>
                              <AlertTriangle size={10} /> {t("dispute.open_chip", "In dispute")}
                            </span>
                          )}
                          <span className="text-xs text-muted font-mono">#{b.id.slice(0,8)}</span>
                        </div>
                        <div className="font-serif-display text-xl truncate">{b.service_title}</div>
                        <div className="text-xs text-muted mt-1">
                          {b.user_name || b.user_email} · {b.start_date} · {t("common.guests", "{{count}} guests", { count: b.guests })} · {format(b.price_total, b.currency)}
                        </div>
                        {b.user_notes && <div className="text-xs text-muted mt-2 italic">"{b.user_notes}"</div>}
                        {inDispute && b.dispute.reason && (
                          <div className="text-xs text-rose-200/80 mt-2 italic line-clamp-2" data-testid={`pdispute-reason-${b.id}`}>
                            "{b.dispute.reason}"
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {b.status === "requested" && (
                          <>
                            <button disabled={busy === b.id} onClick={() => act(b.id, "provider-confirm")} className="btn btn-primary text-[12px]" data-testid={`btn-confirm-${b.id}`}>
                              {t("provider_dash.confirm_availability", "Confirm availability")}
                            </button>
                            <button disabled={busy === b.id} onClick={() => act(b.id, "provider-reject")} className="btn btn-outline text-[12px] text-rose-300" data-testid={`btn-reject-${b.id}`}>
                              {t("provider_dash.decline_request", "Decline")}
                            </button>
                          </>
                        )}
                        {canDispute && (
                          <button onClick={() => setDisputeBooking(b)} className="btn btn-ghost text-[12px] text-rose-300" data-testid={`btn-pdispute-${b.id}`}>
                            <AlertTriangle size={12} /> {t("dispute.open_btn", "Open dispute")}
                          </button>
                        )}
                        {!["cancelled_by_user", "provider_rejected"].includes(b.status) && (
                          <button onClick={() => setChatBookingId(b.id)} className="btn btn-ghost text-[12px]" data-testid={`btn-pmessage-${b.id}`}>
                            <MessageSquare size={12} /> {t("provider_dash.message_user", "Message guest")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Services */}
      {tab === "services" && (
        <div data-testid="prov-services">
          <div className="luxe-card p-4 md:p-5 mb-5 flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
              <input
                value={sQuery}
                onChange={(e) => setSQuery(e.target.value)}
                placeholder={t("provider_dash.services_search_ph", "Search title, description, location…")}
                className="input-bare !pl-10"
                data-testid="prov-services-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {SERVICE_FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setSFilter(f.id)}
                  className={`tab-pill ${sFilter === f.id ? "active" : ""}`}
                  data-testid={`prov-services-filter-${f.id}`}
                >
                  {t(`provider_dash.${f.label_key}`, f.fallback)}
                </button>
              ))}
            </div>
            <button onClick={openCreate} className="btn btn-primary" data-testid="btn-add-service">
              <Plus size={16} /> {t("provider_dash.add_service_btn", "New service")}
            </button>
          </div>

          {filteredServices.length === 0 ? (
            <div className="luxe-card p-10 text-center text-muted text-sm">
              {services.length === 0
                ? t("provider_dash.no_services_body", "Add your first service. We'll review and publish within 24 hours.")
                : t("provider_dash.no_match_filters", "No services match your filters.")}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredServices.map((s) => (
                <ServiceRow
                  key={s.id}
                  s={s}
                  busy={busy}
                  onEdit={openEdit}
                  onArchive={archiveService}
                  onDelete={deleteService}
                  onCalendar={(svc) => setCalendarService(svc)}
                  format={format}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Team */}
      {tab === "team" && (
        <TeamSection services={services} />
      )}

      {/* Wallet */}
      {tab === "wallet" && (
        <WalletSection />
      )}

      {/* Public page tab */}
      {tab === "page" && (
        <div data-testid="prov-page-tab" className="luxe-card p-6 md:p-8">
          <div className="eyebrow">{t("provider_dash.tab_page", "Public page")}</div>
          <h2 className="font-serif-display text-3xl mt-2">
            {t("provider_dash.page_intro_title", "Your shareable provider page.")}
          </h2>
          <p className="text-sm text-muted mt-3 max-w-2xl">
            {t("provider_dash.page_intro_body", "A beautiful, public landing page that lists every published service, neatly grouped by category. Send the link to clients, embed it in your bio, drop it in DMs.")}
          </p>

          {p.status === "approved" ? (
            <div className="mt-6 grid md:grid-cols-[1fr,auto] gap-3 items-center">
              <code className="luxe-card px-4 py-3 text-sm font-mono break-all" data-testid="prov-page-link">
                {publicLink || "—"}
              </code>
              <div className="flex gap-2">
                <button onClick={copyShare} className="btn btn-primary" data-testid="prov-page-copy">
                  {shareCopied ? <Check size={14} /> : <Share2 size={14} />}
                  {shareCopied ? t("provider_dash.share_copied", "Link copied") : t("public_provider.share_link", "Share my page")}
                </button>
                <Link to={`/p/${p.slug || p.id}`} className="btn btn-outline" data-testid="prov-page-view">
                  <ExternalLink size={14} /> {t("provider_dash.view_public_page", "View")}
                </Link>
              </div>
            </div>
          ) : (
            <div className="mt-6 luxe-card p-5 text-sm text-muted">
              {t("provider_dash.page_pending_review", "Your provider account is pending review. Once approved by our concierge team, your public page goes live automatically.")}
            </div>
          )}
        </div>
      )}

      {formMode && (
        <ServiceFormModal
          mode={formMode}
          service={editingService}
          destinations={destinations}
          categories={categories}
          onClose={closeForm}
        />
      )}

      {disputeBooking && (
        <OpenDisputeModal
          booking={disputeBooking}
          onClose={(opened) => {
            setDisputeBooking(null);
            if (opened) {
              setFlash(t("dispute.submitted_flash", "Dispute submitted. Our concierge will review within 24 hours."));
              setTimeout(() => setFlash(null), 5000);
              reload();
            }
          }}
        />
      )}

      {chatBookingId && (
        <ChatDrawer bookingId={chatBookingId} onClose={() => setChatBookingId(null)} />
      )}

      {calendarService && (
        <ServiceCalendarModal
          service={calendarService}
          onClose={() => setCalendarService(null)}
        />
      )}
    </div>
  );
}

function ServiceRow({ s, busy, onEdit, onArchive, onDelete, onCalendar, format, t }) {
  const cover = s.images?.[0];
  const isPublished = s.status === "published";
  const previewLabel = isPublished
    ? t("provider_dash.view_service", "View live")
    : t("provider_dash.preview_service", "Preview");
  return (
    <div className="luxe-card p-4 md:p-5 flex flex-col sm:flex-row gap-4" data-testid={`pserv-${s.id}`}>
      <Link
        to={`/services/${s.id}`}
        className="sm:w-40 sm:h-28 w-full h-44 rounded-xl overflow-hidden bg-ink-deep/40 shrink-0 grid place-items-center group/cover relative"
        data-testid={`pserv-cover-${s.id}`}
        aria-label={previewLabel}
      >
        {cover ? (
          <img src={cover} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover/cover:scale-105" loading="lazy" />
        ) : (
          <ImageIcon size={28} className="text-muted/40" />
        )}
        <span className="absolute inset-0 bg-ink-deep/0 group-hover/cover:bg-ink-deep/40 transition-colors flex items-center justify-center opacity-0 group-hover/cover:opacity-100">
          <span className="chip text-[10px] bg-black/60 border-white/20">
            <Eye size={10} /> {previewLabel}
          </span>
        </span>
      </Link>
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`chip ${isPublished ? "success" : s.status === "archived" ? "" : "warning"}`}>{s.status}</span>
          {s.is_featured && <span className="chip info">{t("service_detail.featured", "Featured")}</span>}
          {s.images?.length ? (
            <span className="text-[11px] text-muted/70">
              {t("provider_dash.images_count_short", "{{count}} photos", { count: s.images.length })}
            </span>
          ) : (
            <span className="text-[11px] text-rose-200/70">
              {t("provider_dash.no_photos_warning", "No photos yet")}
            </span>
          )}
        </div>
        <Link to={`/services/${s.id}`} className="font-serif-display text-xl truncate hover:text-teal transition-colors" data-testid={`pserv-title-${s.id}`}>
          {s.title}
        </Link>
        <p className="text-xs text-muted mt-1 line-clamp-2">{s.description}</p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="font-serif-display text-2xl">{format(s.base_price, s.currency)}</div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/services/${s.id}`} className="btn btn-ghost text-[12px] !px-2 !py-1" data-testid={`btn-preview-${s.id}`}>
              <Eye size={12} /> {previewLabel}
            </Link>
            <button onClick={() => onCalendar(s)} disabled={busy === s.id} className="btn btn-ghost text-[12px] !px-2 !py-1" data-testid={`btn-calendar-${s.id}`}>
              <Calendar size={12} /> {t("provider_dash.calendar_service", "Calendar")}
            </button>
            <button onClick={() => onEdit(s)} disabled={busy === s.id} className="btn btn-ghost text-[12px] !px-2 !py-1" data-testid={`btn-edit-${s.id}`}>
              <Pencil size={12} /> {t("provider_dash.edit_service", "Edit")}
            </button>
            {s.status !== "archived" ? (
              <button onClick={() => onArchive(s, true)} disabled={busy === s.id} className="btn btn-ghost text-[12px] !px-2 !py-1" data-testid={`btn-archive-${s.id}`}>
                <Archive size={12} /> {t("provider_dash.archive_service", "Archive")}
              </button>
            ) : (
              <button onClick={() => onArchive(s, false)} disabled={busy === s.id} className="btn btn-ghost text-[12px] !px-2 !py-1" data-testid={`btn-unarchive-${s.id}`}>
                <Undo2 size={12} /> {t("provider_dash.unarchive_service", "Resubmit")}
              </button>
            )}
            <button onClick={() => onDelete(s)} disabled={busy === s.id} className="btn btn-ghost text-[12px] !px-2 !py-1 text-rose-300" data-testid={`btn-delete-${s.id}`}>
              <Trash2 size={12} /> {t("provider_dash.delete_service", "Delete")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
